import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { useEffect, useState } from "react";
import { ref, set, update } from "firebase/database";
import { ISource } from "../@types/source";
import { db } from "../firebase";
import { useFirebase } from "../FbContex";
import { allSources, kindOf, nextOrder } from "../lib/selectors";
import { SourceKind } from "../enum";
import { prune } from "../lib/node";
import { TextField, NumberField, SelectField, SwitchField } from "./form/Fields";
import { DIALOG_BACKDROP, DIALOG_PANEL } from "./tableStyles";

/** RTDB forbids these in a key. The id is typed by hand, so this is required. */
const BAD_KEY = /[.$#[\]/]/;
// "engines" is a tombstone: the abandoned admin path, kept so nobody
// creates a source there while stale rows may still sit under it.
const RESERVED = ["sources", "engines", "rooms", "devices", "users"];

const hasControlChar = (s: string) =>
  s.split("").some((c) => {
    const n = c.charCodeAt(0);
    return n < 32 || n === 127;
  });

type Param = {
  source: ISource | null;
  close: () => void;
};

export default function SourceForm({ source, close }: Param) {
  const { sources, state } = useFirebase();
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [ip, setIp] = useState("");
  const [kind, setKind] = useState<SourceKind>(SourceKind.ENGINE);
  const [disabled, setDisabled] = useState(false);
  const [order, setOrder] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const editing = !!source?.id;

  useEffect(() => {
    if (!source) return;
    setId(source.id || "");
    setLabel(source.label || "");
    setIp(source.ip || "");
    setKind(kindOf(source));
    setDisabled(source.disabled ?? false);
    setOrder(source.order ?? nextOrder(allSources(sources)));
    setError("");
    // Only reset when a different source is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const save = () => {
    const key = id.trim();

    if (!editing) {
      if (!id) return setError("Path is required.");
      if (id !== key) return setError("No leading or trailing whitespace.");
      if (BAD_KEY.test(key) || hasControlChar(key))
        return setError("A path cannot contain . $ # [ ] / or control characters.");
      if (key.startsWith(".")) return setError("A path cannot start with a dot.");
      if (RESERVED.includes(key))
        return setError(`"${key}" is used by the app itself.`);
      if (sources[key]) return setError(`Source "${key}" already exists.`);
    }
    if (!label.trim()) return setError("Label is required.");

    setError("");
    setLoading(true);
    const data = prune({
      label: label.trim(),
      ip: ip.trim() || undefined,
      kind,
      disabled: disabled || undefined,
      order: order ?? nextOrder(allSources(sources)),
    });

    // The id IS the key, so create uses set() at a known path -- push() would
    // be wrong by construction. Edit uses update() and never touches the id.
    (editing
      ? update(ref(db, `sources/${source!.id}`), data)
      : set(ref(db, `sources/${key}`), data)
    )
      .then(close, (err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const noLiveData = !editing && !!id.trim() && !(id.trim() in (state ?? {}));

  return (
    <Dialog open={!!source} onClose={close} className="relative z-50">
      <DialogBackdrop transition className={DIALOG_BACKDROP} />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <DialogPanel transition className={DIALOG_PANEL}>
            <div className="py-4 flex justify-between text-xl items-center">
              <h3 className="font-semibold opacity-70">
                {editing ? "Edit" : "Add"} Source
              </h3>
              <button className="text-red-400" onClick={close}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="p-5 border-y border-gray-500/50 flex-col space-y-5">
              <TextField
                label="Path"
                value={id}
                onChange={setId}
                disabled={editing}
                hint={
                  editing
                    ? "Fixed -- this is the RTDB path. To rename, create the new source, then delete this one and move its devices across."
                    : `Metadata only. The live tree at /${
                        id.trim() || "<path>"
                      } is never created, moved or deleted by this form.`
                }
              />
              {noLiveData && (
                <div className="text-xs text-amber-500">
                  No live data at /{id.trim()} yet. That is fine if the hardware
                  is not online.
                </div>
              )}
              <TextField label="Label" value={label} onChange={setLabel} />
              <TextField
                label="IP"
                value={ip}
                onChange={setIp}
                hint="Only used by local mode. Empty means no LAN endpoint."
              />
              <SelectField
                label="Kind"
                value={kind}
                onChange={(v) => setKind(v as SourceKind)}
                options={Object.values(SourceKind).map((k) => ({
                  value: k,
                  label: k,
                }))}
                placeholder=""
                hint="Engine: one ESP board that streams this tree and acts on it. Telemetry: a shared aggregate the boards write into and only this app reads."
              />
              <SwitchField
                label="Disabled"
                checked={disabled}
                onChange={setDisabled}
                hint="Stops the app subscribing to the tree at all."
              />
              <NumberField label="Order" value={order} onChange={setOrder} />
              {error && <div className="text-sm text-red-400">{error}</div>}
            </div>

            <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                disabled={loading}
                type="button"
                onClick={save}
                className="inline-flex w-full justify-center rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 sm:ml-3 sm:w-32 disabled:opacity-40"
              >
                {loading ? "..." : "Save"}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={close}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-slate-500/20 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-slate-500/40 hover:bg-slate-500/30 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
